import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs } from 'antd';
import { BgColorsOutlined, SettingOutlined } from '@ant-design/icons';
import { Shield, User, Key, Settings } from 'iconoir-react';
import { UsersPage } from './UsersPage';
import { ApiKeysPage } from './ApiKeysPage';
import { OrganizationsPage } from './OrganizationsPage';
import { SessionsPage } from './SessionsPage';
import { AuditLogsPage } from './AuditLogsPage';
import AppConfigurationSettings from '../../components/AppConfigurationSettings';
import AppearanceSettings from '../../components/AppearanceSettings';

const { TabPane } = Tabs;

export function IAMPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active view from URL path
  const getActiveView = () => {
    const path = location.pathname;
    if (path.includes('/iam/users')) return 'users';
    if (path.includes('/iam/api-keys')) return 'api-keys';
    if (path.includes('/iam/organizations')) return 'organizations';
    if (path.includes('/iam/sessions')) return 'sessions';
    if (path.includes('/iam/audit')) return 'audit';
    if (path.includes('/iam/app-configuration')) return 'app-configuration';
    if (path.includes('/iam/settings')) return 'settings';
    return 'overview';
  };

  const activeView = getActiveView();

  // Render full page for users and api-keys
  if (activeView === 'users') {
    return (
      <div>
        <UsersPage />
      </div>
    );
  }

  if (activeView === 'api-keys') {
    return (
      <div>
        <ApiKeysPage />
      </div>
    );
  }

  if (activeView === 'organizations') {
    return (
      <div>
        <OrganizationsPage />
      </div>
    );
  }

  if (activeView === 'sessions') {
    return (
      <div>
        <SessionsPage />
      </div>
    );
  }

  if (activeView === 'audit') {
    return (
      <div>
        <AuditLogsPage />
      </div>
    );
  }

  if (activeView === 'app-configuration') {
    return (
      <div className="min-h-screen p-6">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">IAM App Configuration</h2>
          <p className="text-sm text-gray-600">
            Manage IAM sidebar items, permissions, and settings for your organization.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="p-6">
            <AppConfigurationSettings applicationFilter="iam" defaultApplication="iam" />
          </div>
        </div>
      </div>
    );
  }

  if (activeView === 'settings') {
    return (
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 600, marginBottom: '8px' }}>
            IAM Settings
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            Configure your IAM application appearance and preferences
          </p>
        </div>

        {/* Tabs Container */}
        <div className="bg-white shadow rounded-lg p-6">
          <Tabs defaultActiveKey="appearance" type="card">
            {/* Appearance Tab */}
            <TabPane
              tab={
                <span>
                  <BgColorsOutlined />
                  {' '}Appearance
                </span>
              }
              key="appearance"
            >
              <div style={{ padding: '16px' }}>
                <AppearanceSettings />
              </div>
            </TabPane>

            {/* General Settings Tab */}
            <TabPane
              tab={
                <span>
                  <SettingOutlined />
                  {' '}General
                </span>
              }
              key="general"
            >
              <div style={{ padding: '16px' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                  General IAM Settings
                </h4>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  Password policies, session management, and security settings will be implemented here.
                </p>
              </div>
            </TabPane>
          </Tabs>
        </div>
      </div>
    );
  }
  
  // Overview Page
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Identity & Access Management</h1>
            <p className="text-gray-600">Manage users, API keys, and security settings</p>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <NavCard
            icon={User}
            title="User Management"
            description="Create, edit, and manage user accounts and their access levels"
            color="violet"
            onClick={() => navigate('/iam/users')}
          />
          <NavCard
            icon={Key}
            title="API Keys"
            description="Generate and manage API keys for programmatic access"
            color="amber"
            onClick={() => navigate('/iam/api-keys')}
          />
          <NavCard
            icon={Settings}
            title="Security Settings"
            description="Configure password policies, MFA, and security rules"
            color="emerald"
            onClick={() => navigate('/iam/settings')}
            comingSoon
          />
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickStat label="Total Users" value="--" icon="👥" />
            <QuickStat label="Active API Keys" value="--" icon="🔑" />
            <QuickStat label="Admin Users" value="--" icon="👑" />
            <QuickStat label="Last Login" value="--" icon="🕐" />
          </div>
          <p className="mt-4 text-sm text-gray-500 text-center">
            Click on a section above to view detailed statistics
          </p>
        </div>
      </div>
    </div>
  );
}

// Navigation Card Component
function NavCard({
  icon: Icon,
  title,
  description,
  color,
  onClick,
  comingSoon,
}: {
  icon: any;
  title: string;
  description: string;
  color: string;
  onClick: () => void;
  comingSoon?: boolean;
}) {
  const colorClasses: Record<string, { bg: string; text: string; gradient: string }> = {
    violet: {
      bg: 'bg-violet-100',
      text: 'text-violet-600',
      gradient: 'from-violet-500 to-purple-600',
    },
    amber: {
      bg: 'bg-amber-100',
      text: 'text-amber-600',
      gradient: 'from-amber-500 to-orange-600',
    },
    emerald: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
      gradient: 'from-emerald-500 to-green-600',
    },
  };

  const colors = colorClasses[color];

  return (
    <button
      onClick={onClick}
      disabled={comingSoon}
      className={`relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-left group ${
        comingSoon ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1'
      }`}
    >
      {comingSoon && (
        <span className="absolute top-4 right-4 px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
          Coming Soon
        </span>
      )}
      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${colors.gradient} shadow-md mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </button>
  );
}

// Quick Stat Component
function QuickStat({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

export default IAMPage;
